<template>
  <div>
    <!-- hero -->
    <div class="page-wrapper pt-0 pb-0">
      <hero
        :show-sign-up="false"
        :full-screen="hero.is_full_screen"
        :title="hero.h1Array"
        :copy="hero.by_lineArray"
        :feature="hero.feature"
        :image-url="hero.image_url"
        :mobi-image-url="hero.mobi_image_url"
        :background-color="hero.background_color"
        :text-color="hero.button_color"
      />
    </div>

    <home-page-carousel
      :data-provider="getRecommendationFetchRequest"
      :track-event-data-accessor="getRecommendedBrandsTrackEventData"
      :title="recommendedBrandsSectionTitle"
      :event_id="eventId"
    />

    <div v-if="showBrandRecommendations === false" id="features" class="pb-30 pt-10 bt bb">
      <div class="container">
        <div class="grid-12">
          <div class="span-4 span-12-sm mb-30-sm text-center px-10 flex flex-direction-column items-center">
            <Icon icon-key="ribbon" :size="75" />
            <h4>Premium Partners</h4>
            <p class="font-size-18 mt-10">
              We only stock the best. Shop thousands of brands vetted for quality and reliability.
            </p>
          </div>
          <div class="span-4 span-12-sm mb-30-sm px-10 flex flex-direction-column items-center">
            <Icon icon-key="hearts" :size="75" />
            <h4>Community</h4>
            <p class="font-size-18 mt-10 text-center">
              Create a custom feed of the brands you want to meet, message, and order from.
            </p>
          </div>
          <div class="span-4 span-12-sm text-center px-10 flex flex-direction-column items-center">
            <Icon icon-key="calendar" :size="75" />
            <h4>Net 60 Payment Terms</h4>
            <p class="font-size-18 mt-10">
              Sell your inventory for two full months before paying the wholesale invoice.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- featured -->
    <div class="page-wrapper page-wrapper--grey bb pb-70 pb-30-sm pt-70 pt-30-sm pb-30-sm">
      <div class="container home-page">
        <featured :is-user-logged="true" />
      </div>
    </div>

    <home-page-carousel
      class="bg-green"
      :title="perfectForTheTitle"
      :track-event-data-accessor="getPerfectForTheTrackEventData"
      :data-provider="fetchMapCollectionsToCarouselItems"
    />

    <!-- new arrivals -->
    <div class="page-wrapper page-wrapper--white pb-130 pt-30-sm pb-30-sm">
      <div class="container home-page">
        <h4 class="mb-30">new arrivals</h4>
        <featured-brands />
      </div>
    </div>
    <!-- about -->
    <about :title="about.title" :copy="about.copy" />
    <!-- BEST SELLERS -->
    <div class="page-wrapper page-wrapper--white bt pb-130 pt-30-sm pb-90-sm">
      <div class="container home-page text-center">
        <h4 class="mb-30 text-left">BEST SELLERS</h4>
        <best-sellers :is-user-logged="false" />
      </div>
    </div>

    <!-- Modal Products Missing -->
    <modal id="modal-shopify-missing-products" :show="modalProductMissing" :dark-bg="true">
      <div class="shopifyProductsModal p-40 pt-60">
        <Icon icon-key="close" hover-type="OPACITY" class="close-icon-confirmation" :on-click="hideModal" />
        <h3>Products Missing</h3>
        <p class="mt-10">Please import products from Shopify to Bulletin to complete your profile.</p>
        <div class="text-right mt-20">
          <bulletin-button class="font-size-16 mr-10" @click="hideModal"> Cancel </bulletin-button>
          <bulletin-button class="font-size-16" variant="primary" @click="hideModal"> Got it </bulletin-button>
        </div>
      </div>
    </modal>

    <!-- Modal Goto Draft Products -->
    <modal id="modal-shopify-goto-draft-products" :show="modalGoToDraftProducts" :dark-bg="true">
      <div class="shopifyProductsModal p-40 pt-60">
        <Icon icon-key="close" hover-type="OPACITY" class="close-container" :on-click="hideModal" />
        <h3>Almost there!</h3>
        <p class="mt-10">
          You’ll be able to receive Bulletin orders once you publish at least one product to your brand page.
        </p>
        <div class="text-right mt-20">
          <bulletin-button class="font-size-16 mr-10" @click="hideModal"> Cancel </bulletin-button>
          <bulletin-button class="font-size-16" variant="primary" @click="onGoToDraftProducts">
            Go to Draft Products
          </bulletin-button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import Featured from './featured/featured.vue'
import Hero from './hero/hero.vue'
import FeaturedBrands from './featuredBrands/featuredBrands.vue'
import BestSellers from './bestSellers/bestSellers.vue'
import HomePageCarousel from './homePageCarousel/homePageCarousel.vue'
import About from './about/about.vue'

import CMSAPI from '../../services/api/contentManagmentApi'
import { recommendationsApi } from '@/services/api/recommendationsApi'
import algoliasearch from 'algoliasearch/lite'
import { homePageHelper } from '../../components/mixin/homePageHelper'
import BrandApi from '../../services/api/brandApi.js'
import { mapGetters } from 'vuex'
import { ProductApi } from '@/api'

const { getHero, getAbout } = new CMSAPI()
const BrandApiService = new BrandApi()

export default {
  components: {
    HomePageCarousel,
    Featured,
    FeaturedBrands,
    BestSellers,
    Hero,
    About
  },
  mixins: [homePageHelper],
  data() {
    return {
      hero: {},
      about: {},
      algoliaClient: null,
      showBrandRecommendations: null,
      recommendedBrandsSectionTitle: 'Perfect for you',
      searchIndexProducts: null,
      modalProductMissing: false,
      modalGoToDraftProducts: false,
      eventId: ''
    }
  },
  computed: {
    ...mapGetters({
      user: 'userState'
    })
  },
  created() {
    this.algoliaClient = algoliasearch(this.$env.algolia().appId, this.$env.algolia().token, {
      timeouts: {
        connect: this.$utils.constants.ALGOLIA_CONNECT_TIMEOUT,
        read: this.$utils.constants.ALGOLIA_READ_TIMEOUT
      }
    })

    this.searchIndexProducts = this.algoliaClient.initIndex(this.$env.getOrigin() + '_products')
  },
  async beforeMount() {
    this.eventId = await this.$utils.tradeShowEnabledFeatures('profile_badges')
    if (this.user && this.user.role === 'BRAND') {
      BrandApiService.getBrandMe().then(async ({ data }) => {
        if (data && data.connect_with_shopify) {
          await this.getProducts()
        }
      })
    }
    getHero()
      .then((response) => {
        const heroData = response.data[0]
        this.hero = {
          ...heroData,
          h1Array: heroData.h1?.split('_%_').map((value) => ({
            value: value
          })),
          by_lineArray: heroData.by_line?.split('_%_').map((value) => ({
            value: value
          }))
        }
      })
      .catch((error) => this.$handleError(this, error))
    getAbout()
      .then((response) => {
        this.about = response.data[0]
      })
      .catch((error) => this.$handleError(this, error))
  },
  methods: {
    getRecommendedBrandsTrackEventData: function ({ brand_name }) {
      return {
        action: 'Bulletin Recommendations Click',
        properties: {
          Location: 'Homepage',
          'Brand Name': brand_name,
          'Retailer Store Name': this.retailer.store_name
        }
      }
    },
    getRecommendationFetchRequest: async function () {
      const user = this.$session.getUserInfo()

      if (user.role === 'BRAND') {
        const { data } = await BrandApiService.getBrandMe()
        if (data.source === 'EXHIBITOR') {
          mixpanel.people.set(
            'Buy on Bulletin',
            (!!data.shopify || data.buy_on_bulletin) && data.ships_from_usa_or_canada
          )
        } else {
          mixpanel.people.set('Buy on Bulletin', true)
        }
      }

      if (user.role !== 'RETAILER') {
        this.showBrandRecommendations = false
        return []
      }

      try {
        const { data } = await recommendationsApi.fetchRecommendedBrands(this.retailer.id)
        const brandsIndex = this.$env.getOrigin() + '_brands'
        const brands = await this.algoliaClient.initIndex(brandsIndex).getObjects(data.brand_ids, {
          attributesToRetrieve: []
        })
        this.recommendedBrandsSectionTitle = `Picked for ${this.retailer.store_name}`
        this.showBrandRecommendations = Boolean(brands.results.length)

        return brands.results.map(({ brand_name, thumbnail_image_url, id, vanity_url_name, event_ids }) => {
          return {
            title: brand_name,
            imageUrl: thumbnail_image_url,
            id,
            routerLinkTo: `/brands/${vanity_url_name}`,
            event_ids
          }
        })
      } catch (error) {
        this.showBrandRecommendations = false
        return []
      }
    },
    async getProducts() {
      const reqObj = {
        distinct: false,
        page: 0,
        hitsPerPage: 10,
        facets: '*,active,brand.id,categories.id,product_status',
        facetFilters: [[], ['brand.id:' + this.user.brand_id], [], [], [], ['product_status: APPROVED']]
      }
      await this.searchIndexProducts.search('', reqObj, this.getProductsCallback)
    },
    async getProductsCallback(err, content) {
      if (content && content.hits && content.hits.length === 0) {
        const draftData = await ProductApi.getAllDraftProductsForBrand({
          brandId: this.user.brand_id,
          page: 1,
          size: 10
        })
        if (draftData && draftData.draft_products && draftData.draft_products.length > 0) {
          this.modalGoToDraftProducts = true
        } else {
          this.modalProductMissing = true
        }
      }
    },
    hideModal() {
      this.modalProductMissing = false
      this.modalGoToDraftProducts = false
    },
    onGoToDraftProducts() {
      this.modalGoToDraftProducts = false
      this.$router.push('/dashboard/drafts')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/sass/base/vars.scss';

.shopifyProductsModal {
  background: $color-white;
  width: 668px;
  .close-icon-confirmation {
    position: absolute;
    right: 30px;
    top: 30px;
  }
}
</style>
